import React, { useState, useEffect } from 'react';
import {
  Users,
  Search,
  FileText,
  Calendar,
  Sparkles,
  TrendingUp,
  Award,
  Download,
  Filter,
  Eye,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  Clock,
  Printer
} from 'lucide-react';
import { Language } from '../../types/kundali';
import { UserProfile } from '../../types/user';
import { getAllKundalisAdmin, StoredKundaliRecord } from '../../lib/kundaliService';
import { getSavedAstrologerBranding } from '../../lib/astrologerBrandingService';

interface AstrologerKundaliStats {
  astrologerId: string;
  astrologerName: string;
  astrologerEmail: string;
  sansthan: string;
  totalKundalis: number;
  lastCreatedAt?: string;
  lastNativeName?: string;
  kundaliRecords: Array<{
    id: string;
    nativeName: string;
    dob: string;
    createdAt: string;
    place?: string;
    chartStyle?: string;
  }>;
}

interface AstrologerKundaliCountViewProps {
  lang: Language;
  currentUser?: UserProfile | null;
  onOpenKundaliReport?: (kundaliId: string) => void;
}

export const AstrologerKundaliCountView: React.FC<AstrologerKundaliCountViewProps> = ({
  lang,
  currentUser,
  onOpenKundaliReport
}) => {
  const isHi = lang === 'hi';
  const [stats, setStats] = useState<AstrologerKundaliStats[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sortBy, setSortBy] = useState<'count' | 'recent' | 'name'>('count');
  const [expandedAstrologer, setExpandedAstrologer] = useState<string | null>(null);

  // Load and aggregate kundalis created per astrologer
  useEffect(() => {
    let isMounted = true;

    async function loadData() {
      setLoading(true);
      try {
        const cloudRecords = await getAllKundalisAdmin();
        const branding = getSavedAstrologerBranding();

        // Standard default baseline astrologers for the institution practice
        const defaultAstrologers: AstrologerKundaliStats[] = branding.name ? [
          {
            astrologerId: 'astro_main',
            astrologerName: branding.name,
            astrologerEmail: 'practice@jyotish.org',
            sansthan: branding.sansthan || 'Vedic Practice',
            totalKundalis: 0,
            kundaliRecords: []
          }
        ] : [];

        // Map cloud records into astrologer buckets
        const map = new Map<string, AstrologerKundaliStats>();
        defaultAstrologers.forEach((a) => map.set(a.astrologerName.toLowerCase(), a));

        if (cloudRecords && cloudRecords.length > 0) {
          cloudRecords.forEach((rec) => {
            const author = rec.createdByName || rec.userEmail || 'Unknown Astrologer';
            const key = author.toLowerCase();

            if (!map.has(key)) {
              map.set(key, {
                astrologerId: rec.userId || `user_${key.replace(/\s+/g, '_')}`,
                astrologerName: author,
                astrologerEmail: rec.userEmail || `${key.replace(/\s+/g, '')}@practice.org`,
                sansthan: branding.sansthan || 'Vedic Practice',
                totalKundalis: 0,
                kundaliRecords: []
              });
            }

            const item = map.get(key)!;
            item.totalKundalis += 1;
            item.kundaliRecords.unshift({
              id: rec.id,
              nativeName: rec.name || 'Untitled Kundali',
              dob: rec.date || 'N/A',
              createdAt: rec.createdAt || new Date().toISOString(),
              place: rec.placeName || 'India',
              chartStyle: rec.chartStyle || 'north'
            });

            if (!item.lastCreatedAt || new Date(rec.createdAt) > new Date(item.lastCreatedAt)) {
              item.lastCreatedAt = rec.createdAt;
              item.lastNativeName = rec.name;
            }
          });
        }

        if (isMounted) {
          setStats(Array.from(map.values()));
          setLoading(false);
        }
      } catch (err) {
        console.error('Failed loading astrologer kundali statistics:', err);
        if (isMounted) setLoading(false);
      }
    }

    loadData();
    return () => {
      isMounted = false;
    };
  }, []);

  // Filter & Sort
  const filteredStats = stats
    .filter((s) => {
      const q = searchTerm.toLowerCase();
      return (
        s.astrologerName.toLowerCase().includes(q) ||
        s.astrologerEmail.toLowerCase().includes(q) ||
        s.sansthan.toLowerCase().includes(q) ||
        s.kundaliRecords.some((k) => k.nativeName.toLowerCase().includes(q))
      );
    })
    .sort((a, b) => {
      if (sortBy === 'count') return b.totalKundalis - a.totalKundalis;
      if (sortBy === 'name') return a.astrologerName.localeCompare(b.astrologerName);
      if (sortBy === 'recent') {
        const timeA = new Date(a.lastCreatedAt || 0).getTime();
        const timeB = new Date(b.lastCreatedAt || 0).getTime();
        return timeB - timeA;
      }
      return 0;
    });

  const totalAllKundalis = stats.reduce((acc, curr) => acc + curr.totalKundalis, 0);
  const topAstrologer = stats.slice().sort((a, b) => b.totalKundalis - a.totalKundalis)[0];

  return (
    <div className="space-y-6">
      {/* 1. Header & KPI Cards */}
      <div className="bg-astro-card border border-astro-border rounded-2xl p-4 sm:p-6 shadow-xs space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-astro-border pb-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-[#C82A1E] text-white shadow-xs">
              <Users className="w-5 h-5 text-amber-200" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold font-serif text-astro-text">
                {isHi ? 'ज्योतिषी कुण्डली निर्माण विवरणी (प्रबंधन रिपोर्ट)' : 'Astrologer Kundali Productivity & Volume Audit'}
              </h2>
              <p className="text-xs text-astro-secondary-text">
                {isHi
                  ? 'प्रत्येक ज्योतिषी द्वारा निर्मित कुण्डलियों की कुल संख्या, हालिया कार्य एवं विस्तृत इतिहास'
                  : 'Comprehensive management report tracking total kundalis created per astrologer across the institution'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-stone-600 bg-stone-100 dark:bg-stone-800 px-3 py-1.5 rounded-lg border border-stone-200 dark:border-stone-700">
              {isHi ? 'प्रबंधन रिपोर्ट' : 'Management Audit View'}
            </span>
          </div>
        </div>

        {/* 3 Metrics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="p-3.5 bg-astro-bg rounded-xl border border-astro-border space-y-1">
            <span className="text-xs text-astro-secondary-text font-medium">
              {isHi ? 'कुल निर्मित कुण्डलियाँ' : 'Total Kundalis Created'}
            </span>
            <div className="text-2xl sm:text-3xl font-black font-mono text-[#C82A1E]">
              {totalAllKundalis}
            </div>
            <p className="text-[10px] text-emerald-600 font-semibold">
              ✓ Across all active practice practitioners
            </p>
          </div>

          <div className="p-3.5 bg-astro-bg rounded-xl border border-astro-border space-y-1">
            <span className="text-xs text-astro-secondary-text font-medium">
              {isHi ? 'सक्रिय ज्योतिषी' : 'Active Astrologers'}
            </span>
            <div className="text-2xl sm:text-3xl font-black font-mono text-astro-text">
              {stats.length}
            </div>
            <p className="text-[10px] text-astro-secondary-text">
              Contributing Acharyas & Consultants
            </p>
          </div>

          <div className="p-3.5 bg-astro-bg rounded-xl border border-astro-border space-y-1">
            <span className="text-xs text-astro-secondary-text font-medium">
              {isHi ? 'शीर्ष योगदानकर्ता' : 'Top Contributing Astrologer'}
            </span>
            <div className="text-lg font-bold text-amber-700 truncate">
              {topAstrologer ? topAstrologer.astrologerName : 'N/A'}
            </div>
            <p className="text-[10px] font-mono text-stone-500">
              {topAstrologer ? `${topAstrologer.totalKundalis} Kundalis Generated` : ''}
            </p>
          </div>
        </div>
      </div>

      {/* 2. Controls & Search Bar */}
      <div className="bg-astro-bg border border-astro-border rounded-xl p-3 flex flex-wrap items-center justify-between gap-3 text-xs">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={isHi ? 'ज्योतिषी, संस्था या जातक के नाम से खोजें...' : 'Search astrologer, sansthan, or client native...'}
            className="w-full pl-9 pr-3 py-1.5 bg-white dark:bg-slate-900 border border-astro-border rounded-lg text-xs text-astro-text focus:outline-none focus:ring-1 focus:ring-[#C82A1E]"
          />
        </div>

        <div className="flex items-center gap-2">
          <span className="font-semibold text-astro-secondary-text hidden sm:inline">Sort:</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className="px-2.5 py-1.5 rounded-lg border border-astro-border bg-white dark:bg-slate-900 text-xs font-semibold text-astro-text focus:outline-none cursor-pointer"
          >
            <option value="count">Most Kundalis (कुण्डली संख्या)</option>
            <option value="recent">Recently Active (हालिया सक्रिय)</option>
            <option value="name">Astrologer Name (नाम अनुसार)</option>
          </select>
        </div>
      </div>

      {/* 3. Main Astrologer List & Count Cards */}
      <div className="space-y-3">
        {filteredStats.map((item) => {
          const isExpanded = expandedAstrologer === item.astrologerId;
          const sharePercentage = totalAllKundalis > 0 ? Math.round((item.totalKundalis / totalAllKundalis) * 100) : 0;

          return (
            <div
              key={item.astrologerId}
              className="bg-astro-card border border-astro-border rounded-2xl p-4 sm:p-5 shadow-xs transition-all hover:border-[#C82A1E]/40 space-y-3"
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                {/* Astrologer Info */}
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-[#C82A1E] text-white flex items-center justify-center font-serif font-bold text-base shadow-xs shrink-0">
                    {item.astrologerName.slice(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm sm:text-base font-bold font-serif text-astro-text">
                        {item.astrologerName}
                      </h3>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 border border-amber-300/60">
                        {item.sansthan}
                      </span>
                    </div>
                    <div className="text-xs text-astro-secondary-text">
                      {item.astrologerEmail} • {item.lastNativeName ? `Latest Native: ${item.lastNativeName}` : 'Active Practitioner'}
                    </div>
                  </div>
                </div>

                {/* Kundali Count Badge & Expand Toggle */}
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="text-xl sm:text-2xl font-black font-mono text-[#C82A1E] leading-none">
                      {item.totalKundalis}
                    </div>
                    <span className="text-[10px] text-astro-secondary-text font-bold uppercase tracking-wider block">
                      {isHi ? 'कुण्डलियाँ निर्मित' : 'Kundalis Created'}
                    </span>
                  </div>

                  <button
                    onClick={() => setExpandedAstrologer(isExpanded ? null : item.astrologerId)}
                    className="p-2 rounded-xl bg-astro-bg border border-astro-border hover:bg-astro-border text-astro-text transition-colors cursor-pointer flex items-center gap-1 text-xs font-semibold"
                    title="Toggle Kundali list"
                  >
                    <span>{isExpanded ? 'Hide' : 'View Charts'}</span>
                    {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>

              {/* Progress bar of institution share */}
              <div className="w-full bg-stone-100 dark:bg-stone-800 h-2 rounded-full overflow-hidden">
                <div
                  className="bg-gradient-to-r from-amber-500 to-[#C82A1E] h-full rounded-full transition-all duration-500"
                  style={{ width: `${Math.max(5, sharePercentage)}%` }}
                  title={`${sharePercentage}% of institutional charts`}
                />
              </div>

              {/* Expandable Chart List */}
              {isExpanded && (
                <div className="pt-3 border-t border-astro-border space-y-2 animate-fadeIn">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-stone-600 dark:text-stone-400">
                    {isHi
                      ? `निर्मित कुण्डलियों का विवरण (${item.kundaliRecords.length})`
                      : `Charts Created by ${item.astrologerName} (${item.kundaliRecords.length})`}
                  </h4>

                  <div className="overflow-x-auto">
                    <table className="w-full text-xs text-left border border-astro-border rounded-lg overflow-hidden">
                      <thead className="bg-astro-bg text-astro-secondary-text font-bold border-b border-astro-border">
                        <tr>
                          <th className="p-2">जातक का नाम (Native)</th>
                          <th className="p-2">जन्म तिथि (DOB)</th>
                          <th className="p-2">जन्म स्थान (Place)</th>
                          <th className="p-2">निर्माण तिथि (Created Date)</th>
                          <th className="p-2 text-right">कार्य (Action)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-astro-border">
                        {item.kundaliRecords.map((rec) => (
                          <tr key={rec.id} className="hover:bg-astro-bg/50">
                            <td className="p-2 font-bold text-astro-text flex items-center gap-1.5">
                              <FileText className="w-3.5 h-3.5 text-[#C82A1E]" />
                              <span>{rec.nativeName}</span>
                            </td>
                            <td className="p-2 font-mono text-astro-secondary-text">{rec.dob}</td>
                            <td className="p-2 text-astro-secondary-text">{rec.place || 'India'}</td>
                            <td className="p-2 text-astro-secondary-text font-mono">
                              {rec.createdAt ? new Date(rec.createdAt).toLocaleDateString() : 'Recent'}
                            </td>
                            <td className="p-2 text-right">
                              <button
                                onClick={() => onOpenKundaliReport?.(rec.id)}
                                className="px-2 py-1 rounded bg-[#C82A1E]/10 hover:bg-[#C82A1E] text-[#C82A1E] hover:text-white text-[10px] font-bold transition-all cursor-pointer inline-flex items-center gap-1"
                              >
                                <Eye className="w-3 h-3" />
                                <span>View / Print</span>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
